import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { Box, Container } from '@mui/system';
import { BILLING_PERMISSIONS, BillingModule, RBAC_ENFORCEMENT_TOGGLE } from '@restore/rebill';
import type { BillingError, BillingFacility, BillingNotification, BillingUser } from '@restore/rebill';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { baseApiUrl, enterpriseRbacEnforcementTreatment } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { useScopeChecker } from 'src/hooks/use-permission';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';

/**
 * Every scopeKey the module gates on, resolved here and handed down as the user's granted set.
 *
 * Derived from the module's own map rather than restated: a scope rebill starts gating on is one
 * this host must already be resolving, and a list maintained by hand would silently lag it.
 */
const BILLING_SCOPE_KEYS = Object.values(BILLING_PERMISSIONS);

/**
 * Host wrapper mounting the rebill BillingModule for the preliminary invoice review (BOPS-131).
 *
 * The screen itself used to live here as ~5,700 lines of provider-portal code, duplicating what is
 * now plugin surface. What is left is the host contract: the portal supplies the Keycloak token, the
 * gateway base URL, the tenant key and the facility scope, and the module owns everything else.
 *
 * Mounted with a trailing wildcard so the module can route its own `:monthKey` segment, the same way
 * the Enterprise Portal mounts Contracts and Financial Periods.
 */
const PreliminaryInvoiceReview = () => {
  const [notification, setNotification] = useState<BillingNotification | null>(null);

  const profile = useSelector((state: any) => state?.getTherapistProfileReducer?.response?.data);
  const assignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);

  //  rebill's API paths already include /api (the gateway routes /api/billing/** and
  //  /api/provider/**), and baseApiUrl ends in /api for the legacy interceptor — strip it so the
  //  module does not double up. Same treatment as the Enterprise Portal hosts.
  const apiBaseUrl = String(baseApiUrl ?? '').replace(/\/api\/?$/, '');

  //  BOPS-209: rebill gates on catalog scopeKeys, not the legacy human names.
  const [isRbacEnforced] = useFeatureToggleWhenReady(enterpriseRbacEnforcementTreatment, 'ENTERPRISE');

  //  BOPS-298: resolve the module's scopes through the portal's own checker rather than reading
  //  `profile.currentScopes` directly. `/provider/v2/profile` returns ProfileResponse, which has no
  //  `currentScopes` field at all — only the human-name `currentPermissions` — so the direct read
  //  was always `[]` and, with enforcement on, denied the screen to every user regardless of role.
  //  `useScopeChecker` carries the dual-emit fallback (scopeKeys when a backend emits them, catalog
  //  display-name match when it does not), the same resolution the nav item and the entry button
  //  already use. Drop the filter for a plain `currentScopes` pass-through once that endpoint
  //  dual-emits.
  const hasScope = useScopeChecker();

  const user = useMemo<BillingUser>(() => {
    //  "Levy, Justin" — the order the rest of the portal names people in.
    const displayName = [profile?.lastName, profile?.firstName]
      .map((part: unknown) => String(part ?? '').trim())
      .filter(Boolean)
      .join(', ');

    return {
      id: String(profile?.uuid ?? profile?.id ?? 'unknown'),
      displayName,
      currentPermissions: BILLING_SCOPE_KEYS.filter(hasScope),
    };
  }, [profile, hasScope]);

  const selectedFacility = useMemo<BillingFacility | null>(() => {
    const facilityUuid = localStorage.getItem('facilityId');
    if (!facilityUuid) return null;

    const match = assignedFacilities?.response?.data?.data?.find(
      (facility: any) => facility.uuid === facilityUuid
    );

    return {
      id: facilityUuid,
      name: String(match?.name ?? ''),
      operatorId: String(match?.operatorUuid ?? match?.operatorId ?? ''),
      //  The review's "last updated" stamp reads on the facility's clock, not the browser's. The
      //  portal already keeps this against the selected facility, so the module is handed it rather
      //  than reaching into host storage itself.
      timeZone: localStorage.getItem('facility_timezone') || undefined,
    };
  }, [assignedFacilities]);

  const handleError = useCallback((error: BillingError) => {
    console.error('[rebill]', error.code, error.message);
  }, []);

  return (
    <>
      <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Container
          maxWidth="xl"
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <BillingModule
            getAccessToken={getAccessToken}
            apiBaseUrl={apiBaseUrl}
            tenantId={getRealmName() ?? ''}
            user={user}
            selectedFacility={selectedFacility}
            featureToggles={{ [RBAC_ENFORCEMENT_TOGGLE]: isRbacEnforced === true }}
            onNotification={setNotification}
            onError={handleError}
          />
        </Container>
      </Box>
      <Snackbar
        open={notification !== null}
        autoHideDuration={5000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={notification?.level ?? 'info'} onClose={() => setNotification(null)}>
          {notification?.message ?? ''}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PreliminaryInvoiceReview;
